import { CopilotThread, CopilotMessage, CopilotFunction, CopilotAssistant } from '../types/database-types';
import { Json } from '@/integrations/supabase/types';
import { logInfo, LogCategory } from '@/utils/logger';

/**
 * Utility functions to map between database models (snake_case) and application models (camelCase)
 */

export function safeJsonToRecord(json: Json | null): Record<string, any> {
  if (json === null) return {};
  if (typeof json === 'object' && json !== null) return json as Record<string, any>;
  return {}; // Return empty object for primitive types
}

export function mapThreadFromDb(dbThread: any): CopilotThread {
  logInfo('Mapping thread from DB', { 
    category: LogCategory.MAPPING, 
    data: { id: dbThread.id, title: dbThread.title }
  });
  
  return {
    id: dbThread.id,
    userId: dbThread.user_id,
    title: dbThread.title,
    lastMsgFromUser: dbThread.last_msg_from_user || '',
    createdAt: dbThread.created_at,
    updatedAt: dbThread.updated_at,
    isPinned: dbThread.is_pinned,
    isArchived: dbThread.is_archived,
    metadata: safeJsonToRecord(dbThread.metadata),
    assistants: dbThread.assistants || [], // Include assistants if joined
  };
}

export function mapMessageFromDb(dbMessage: any, skipLogging: boolean = false): CopilotMessage {
  // Only log individual messages when not doing bulk operations
  if (!skipLogging) {
    logInfo('Mapping message from DB', { 
      category: LogCategory.MAPPING, 
      data: { id: dbMessage.id, role: dbMessage.role }
    });
  }
  
  return {
    id: dbMessage.id,
    threadId: dbMessage.thread_id,
    role: dbMessage.role || '',
    content: dbMessage.content,
    createdAt: dbMessage.created_at,
    functionCall: dbMessage.function_call,
    functionResponse: dbMessage.function_response,
    tokensUsed: dbMessage.tokens_used,
    metadata: safeJsonToRecord(dbMessage.metadata),
  };
}

export function mapFunctionFromDb(dbFunction: any): CopilotFunction {
  logInfo('Mapping function from DB', { 
    category: LogCategory.MAPPING, 
    data: { id: dbFunction.id, name: dbFunction.name }
  });
  
  return {
    id: dbFunction.id,
    name: dbFunction.name,
    description: dbFunction.description || '',
    parameters: dbFunction.parameters,
    category: dbFunction.category,
    isEnabled: dbFunction.is_enabled,
    createdAt: dbFunction.created_at,
    updatedAt: dbFunction.updated_at,
  };
}

export function mapAssistantFromDb(dbAssistant: any, skipLogging: boolean = false): CopilotAssistant {
  // Add this at the top of the function
  const DEBUG_ASSISTANT_MAPPING = false; // Set to true only when debugging
  
  if (DEBUG_ASSISTANT_MAPPING) {
    console.log("=== MAPPING ASSISTANT ===");
    console.log("Raw assistant data:", dbAssistant);
  }
  
  // Regular logging for metrics (skip during bulk operations)
  if (!skipLogging) {
    logInfo('Mapping assistant from DB', { 
      category: LogCategory.MAPPING, 
      data: { 
        id: dbAssistant.id, 
        name: dbAssistant.name,
        has_profile_pic: !!dbAssistant.profile_picture_url
      }
    });
  }
  
  const mappedAssistant = {
    id: dbAssistant.id,
    name: dbAssistant.name,
    description: dbAssistant.description || '',
    openai_assistant_id: dbAssistant.openai_assistant_id,
    is_active: dbAssistant.is_active,
    created_at: dbAssistant.created_at,
    updated_at: dbAssistant.updated_at,
    profile_picture_url: dbAssistant.profile_picture_url,
    specialization: dbAssistant.specialization,
  };
  
  if (DEBUG_ASSISTANT_MAPPING) {
    console.log("Mapped assistant:", mappedAssistant);
    console.log("=== END MAPPING ASSISTANT ===");
  }
  
  return mappedAssistant;
}

export function mapThreadToDb(thread: Partial<CopilotThread>): any {
  logInfo('Mapping thread to DB', { 
    category: LogCategory.MAPPING, 
    data: { id: thread.id }
  });
  
  return {
    ...thread.id && { id: thread.id },
    ...thread.userId && { user_id: thread.userId },
    ...thread.title && { title: thread.title },
    ...thread.lastMsgFromUser !== undefined && { last_msg_from_user: thread.lastMsgFromUser },
    ...thread.isPinned !== undefined && { is_pinned: thread.isPinned },
    ...thread.isArchived !== undefined && { is_archived: thread.isArchived },
    ...thread.metadata && { metadata: thread.metadata },
  };
}

export function mapMessageToDb(message: Partial<CopilotMessage>): any {
  logInfo('Mapping message to DB', { 
    category: LogCategory.MAPPING, 
    data: { id: message.id, role: message.role }
  });
  
  const dbMessage: any = {
    ...message.id && { id: message.id },
    ...message.threadId && { thread_id: message.threadId },
    ...message.role && { role: message.role },
    ...message.content !== undefined && { content: message.content },
    ...message.functionCall && { function_call: message.functionCall },
    ...message.functionResponse && { function_response: message.functionResponse },
    ...message.tokensUsed !== undefined && { tokens_used: message.tokensUsed },
    ...message.metadata && { metadata: message.metadata },
  };

  // Extract assistant_id from metadata if present
  if (message.metadata?.assistant?.id) {
    dbMessage.assistant_id = message.metadata.assistant.id;
  }

  return dbMessage;
}

export function mapAssistantToDb(assistant: Partial<CopilotAssistant>): any {
  logInfo('Mapping assistant to DB', { 
    category: LogCategory.MAPPING, 
    data: { id: assistant.id, name: assistant.name }
  });
  
  return {
    ...assistant.id && { id: assistant.id },
    ...assistant.name && { name: assistant.name },
    ...assistant.description && { description: assistant.description },
    ...assistant.openai_assistant_id && { openai_assistant_id: assistant.openai_assistant_id },
    ...assistant.is_active !== undefined && { is_active: assistant.is_active },
    ...assistant.profile_picture_url && { profile_picture_url: assistant.profile_picture_url },
  };
}
